import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { JSONContent } from '@tiptap/react';
import aviso, { textoDoErro, SEM_SERVIDOR } from '../lib/aviso';
import { LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection, Switch } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import Select from '../components/Select';
import Dialog from '../components/Dialog';
import { ListRow, PerPage, RowAction, RowActions, RowConfirm, CardConfirm, ListCard, ListCards } from '../components/ListKit';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import { ehAdministradorDaEmpresa } from '../config/permissions';
import ModeloDocumentoEditor from '../components/documentos/ModeloDocumentoEditor';
import FolhaDocumento, { MesaDaFolha } from '../components/documentos/FolhaDocumento';
import { useEmpresaDaFolha } from '../components/documentos/useEmpresaDaFolha';
import NovoModeloModal, { type EscolhaModelo } from '../components/documentos/NovoModeloModal';
import { DOCUMENTO_VAZIO, lerConteudo } from '../components/documentos/conteudoModelo';
import {
    ModeloDocumentoService, TipoDocumento, NOME_TIPO_DOCUMENTO, OPCOES_TIPO_DOCUMENTO,
    type ModeloDocumento, type VariavelDocumento,
} from '../services/ModeloDocumentoService';
import './ModelosDocumentoPage.css';

const POR_PAGINA = [25, 50, 100];
type Modo = 'ver' | 'editar';

interface Rascunho {
    nome: string;
    tipo: TipoDocumento;
    validadeDias: string;
    ativo: boolean;
    conteudo: JSONContent;
}

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const rascunhoDe = (m: ModeloDocumento | null): Rascunho => ({
    nome: m?.nome ?? '',
    tipo: m?.tipo ?? TipoDocumento.Proposta,
    validadeDias: m?.validadeDias ? String(m.validadeDias) : '',
    ativo: m?.ativo ?? true,
    conteudo: m ? lerConteudo(m.conteudoJson) : DOCUMENTO_VAZIO,
});

/**
 * Modelos de proposta, orçamento e contrato (05/10/2026). Mesmo molde de
 * Motivos de perda: lista do ListKit, painel de dois modos. O que é só desta
 * tela é o editor de texto rico com variáveis, que vive dentro do painel largo.
 *
 * Quem cria e edita é o administrador da empresa (a API exige a
 * AdministradorEmpresaPolicy); vendedor abre para ver. A tela inteira só
 * aparece com a funcionalidade `modelos` ligada (ROUTE_FEATURES).
 */
const ModelosDocumentoPage = () => {
    const celular = useCelular();
    const admin = ehAdministradorDaEmpresa();
    const [modelos, setModelos] = useState<ModeloDocumento[]>([]);
    const [variaveis, setVariaveis] = useState<VariavelDocumento[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [recorte, gravarRecorte] = useRecorte('md:recorte', { busca: '', pagina: 1, porPagina: 25 });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => { gravarRecorte({ busca, pagina, porPagina }); }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<ModeloDocumento | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(rascunhoDe(null));
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);
    const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
    const [galeriaAberta, setGaleriaAberta] = useState(false);
    /* Muda a cada "Novo": o editor do rascunho novo tem sempre id 0, e sem
       isto um segundo pronto abriria com o texto do primeiro. */
    const [novoSeq, setNovoSeq] = useState(0);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const [r, v] = await Promise.all([
                ModeloDocumentoService.buscarTodos(),
                ModeloDocumentoService.variaveis(),
            ]);
            if (r.sucesso) setModelos(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar os modelos.');
            if (v.sucesso) setVariaveis(v.dados || []);
        } catch (e) {
            aviso.erro(textoDoErro(e, SEM_SERVIDOR));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t ? modelos.filter(m => m.nome.toLowerCase().includes(t)) : modelos;
    }, [modelos, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const abrirVer = (m: ModeloDocumento) => { setAberto(m); setRascunho(rascunhoDe(m)); setModo('ver'); setErro(''); };
    const abrirEdicao = (m: ModeloDocumento) => { setAberto(m); setRascunho(rascunhoDe(m)); setModo('editar'); setErro(''); };
    /* "Novo modelo" abre a galeria de prontos (Direção A da proposta
       modelos-de-documento-biblioteca.html). Ela não cria nada: devolve o
       rascunho, e o painel abre em edição — só vira registro no Salvar. */
    const abrirNovo = () => setGaleriaAberta(true);
    const aoEscolherNovo = (e: EscolhaModelo | null) => {
        setGaleriaAberta(false);
        const r = e
            ? { nome: e.nome, tipo: e.tipo, validadeDias: e.validadeDias ? String(e.validadeDias) : '', ativo: e.ativo, conteudo: e.conteudo }
            : rascunhoDe(null);
        setAberto({ modeloDocumentoId: 0, nome: '', tipo: r.tipo, conteudoJson: '', validadeDias: null, ativo: r.ativo, dataCriacao: '', dataAtualizacao: '' });
        setRascunho(r); setModo('editar'); setErro('');
        setNovoSeq(n => n + 1);
    };
    const fechar = () => { setAberto(null); setErro(''); setConfirmandoExclusao(false); };

    const ehNovo = modo === 'editar' && aberto?.modeloDocumentoId === 0;

    /* A folha (ver e editar) traz o cabeçalho da empresa e os valores de exemplo. */
    const empresa = useEmpresaDaFolha();
    const exemplos = useMemo(() => Object.fromEntries(variaveis.map(v => [v.chave, v.exemplo])), [variaveis]);
    const validadeNum = Number(rascunho.validadeDias);
    const validadeDaFolha = Number.isInteger(validadeNum) && validadeNum > 0 ? validadeNum : null;

    const mudar = <K extends keyof Rascunho>(campo: K, valor: Rascunho[K]) => {
        setRascunho(r => ({ ...r, [campo]: valor }));
        setErro('');
    };

    const salvar = async () => {
        const nome = rascunho.nome.trim();
        if (!nome) { setErro('Informe o nome do modelo.'); return; }
        const validade = rascunho.validadeDias.trim() === '' ? null : Number(rascunho.validadeDias);
        if (validade !== null && (!Number.isInteger(validade) || validade <= 0)) {
            setErro('A validade padrão deve ser um número de dias maior que zero, ou ficar em branco.');
            return;
        }
        const repetido = modelos.some(m =>
            m.modeloDocumentoId !== aberto?.modeloDocumentoId && m.nome.trim().toLowerCase() === nome.toLowerCase());
        if (repetido) { setErro('Já existe um modelo com esse nome.'); return; }

        const dto = { nome, tipo: rascunho.tipo, validadeDias: validade, ativo: rascunho.ativo, conteudoJson: JSON.stringify(rascunho.conteudo) };
        setSalvando(true);
        try {
            const r = ehNovo
                ? await ModeloDocumentoService.criar(dto)
                : await ModeloDocumentoService.atualizar({ ...dto, modeloDocumentoId: aberto!.modeloDocumentoId });
            aviso.ok(ehNovo ? 'Modelo criado.' : 'Modelo atualizado.');
            await carregar();
            if (r.dados) abrirVer(r.dados); else fechar();
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar o modelo.'));
        } finally {
            setSalvando(false);
        }
    };

    const excluir = async (alvo = aberto) => {
        if (!alvo?.modeloDocumentoId) return;
        setSalvando(true);
        try {
            await ModeloDocumentoService.remover(alvo.modeloDocumentoId);
            aviso.ok('Modelo excluído.');
            await carregar(); fechar();
        } catch (e) {
            aviso.erro(textoDoErro(e, SEM_SERVIDOR));
        } finally {
            setSalvando(false);
            setAConfirmar(null);
        }
    };

    const COLUNAS = '2fr 110px 110px 90px 120px 78px';

    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {modelos.length === 0 ? 'Nenhum modelo cadastrado' : 'Nada com esse termo'}
            </div>
            <p className="lk-blank-text">
                {modelos.length === 0
                    ? (admin
                        ? 'Com um modelo, o vendedor emite a proposta em PDF de dentro da oportunidade, com os dados do contato e os itens já preenchidos.'
                        : 'Peça a um administrador para cadastrar o primeiro modelo de proposta.')
                    : 'Ajuste a busca para ver os outros.'}
            </p>
            {modelos.length === 0 && admin && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo modelo
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    const ativoTag = (m: ModeloDocumento) =>
        m.ativo ? <span className="md-tag is-on">Ativo</span> : <span className="md-tag">Inativo</span>;

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Modelos de documento"
                subtitle="Propostas, orçamentos e contratos que o vendedor emite em PDF de dentro da oportunidade."
                actions={!admin || (!isLoading && modelos.length === 0) ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo modelo
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar modelo…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {modelos.length} {modelos.length === 1 ? 'modelo' : 'modelos'}
                    </span>
                </div>

                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(m => (
                                    aConfirmar === m.modeloDocumentoId ? (
                                        <CardConfirm key={m.modeloDocumentoId}
                                            question={`Excluir "${m.nome}"?`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(m)} />
                                    ) : (
                                        <ListCard
                                            key={m.modeloDocumentoId}
                                            titulo={m.nome}
                                            apoio={`${NOME_TIPO_DOCUMENTO[m.tipo]} · ${m.ativo ? 'Ativo' : 'Inativo'}`}
                                            selected={aberto?.modeloDocumentoId === m.modeloDocumentoId}
                                            label={`Abrir ${m.nome}`}
                                            onOpen={() => abrirVer(m)}
                                            acoes={admin && (
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir modelo" danger
                                                    onClick={() => setAConfirmar(m.modeloDocumentoId)} />
                                            )}
                                        />
                                    )
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                <div className="lk-head">Modelo</div>
                                <div className="lk-head">Tipo</div>
                                <div className="lk-head">Validade</div>
                                <div className="lk-head">Situação</div>
                                <div className="lk-head">Atualizado</div>
                                <div className="lk-head lk-head-acts">Ações</div>
                            </div>
                            <div className="lk-body">
                                {isLoading
                                    ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : visiveis.map(m => (
                                        aConfirmar === m.modeloDocumentoId ? (
                                            <RowConfirm key={m.modeloDocumentoId}
                                                question={`Excluir "${m.nome}"?`}
                                                busy={salvando}
                                                onCancel={() => setAConfirmar(null)}
                                                onConfirm={() => excluir(m)} />
                                        ) : (
                                            <ListRow key={m.modeloDocumentoId} columns={COLUNAS}
                                                selected={aberto?.modeloDocumentoId === m.modeloDocumentoId}>
                                                <span className="lk-cell lk-strong"><span>{m.nome}</span></span>
                                                <span className="lk-cell lk-muted"><span>{NOME_TIPO_DOCUMENTO[m.tipo]}</span></span>
                                                <span className="lk-cell lk-muted"><span>{m.validadeDias ? `${m.validadeDias} dias` : '—'}</span></span>
                                                <span className="lk-cell"><span>{ativoTag(m)}</span></span>
                                                <span className="lk-cell lk-muted"><span>{dataCurta(m.dataAtualizacao)}</span></span>
                                                <RowActions>
                                                    <RowAction icon={<LuEye size={14} />} label="Ver modelo" onClick={() => abrirVer(m)} />
                                                    {admin && (
                                                        <RowAction icon={<LuTrash2 size={14} />} label="Excluir modelo" danger
                                                            onClick={() => setAConfirmar(m.modeloDocumentoId)} />
                                                    )}
                                                </RowActions>
                                            </ListRow>
                                        )
                                    ))}
                            </div>
                        </div>

                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                wide
                title={modo === 'editar'
                    ? (rascunho.nome.trim() || (ehNovo ? 'Novo modelo' : 'Modelo'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto && !ehNovo
                    ? `${NOME_TIPO_DOCUMENTO[aberto.tipo]} · atualizado em ${dataCurta(aberto.dataAtualizacao)}`
                    : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={admin && aberto && !ehNovo && modo === 'ver' && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={modo === 'editar' ? (
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => setConfirmandoExclusao(true)} disabled={salvando}>
                                Excluir
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                ) : undefined}
            >
                {aberto && modo === 'ver' && (
                    <>
                        <PanelSection title="Modelo">
                            <div className="md-kv">
                                <span className="md-k">Tipo</span><span className="md-v">{NOME_TIPO_DOCUMENTO[aberto.tipo]}</span>
                                <span className="md-k">Validade padrão</span><span className="md-v">{aberto.validadeDias ? `${aberto.validadeDias} dias` : 'Sem validade'}</span>
                                <span className="md-k">Situação</span><span className="md-v">{ativoTag(aberto)}</span>
                            </div>
                        </PanelSection>
                        <PanelSection title="Conteúdo">
                            <MesaDaFolha>
                                <FolhaDocumento conteudo={rascunho.conteudo} tipo={aberto.tipo}
                                    validadeDias={aberto.validadeDias ?? null}
                                    empresa={empresa} exemplos={exemplos} />
                            </MesaDaFolha>
                            <span className="md-hint">
                                Como sai o PDF, com dados de exemplo. O que está em azul muda a cada oportunidade.
                            </span>
                        </PanelSection>
                    </>
                )}

                {aberto && modo === 'editar' && (
                    <>
                        <PanelSection title="Modelo">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="md-nome">Nome</label>
                                <input id="md-nome" className="rp-input" type="text" autoFocus
                                    value={rascunho.nome} placeholder="Ex.: Proposta padrão"
                                    onChange={e => mudar('nome', e.target.value)} />
                            </div>
                            <div className="md-linha">
                                <div className="rp-field">
                                    <label className="rp-label">Tipo</label>
                                    <Select value={String(rascunho.tipo)} options={OPCOES_TIPO_DOCUMENTO}
                                        onChange={v => mudar('tipo', Number(v) as TipoDocumento)} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="md-validade">Validade padrão (dias)</label>
                                    <input id="md-validade" className="rp-input" type="number" min={1}
                                        value={rascunho.validadeDias} placeholder="Sem validade"
                                        onChange={e => mudar('validadeDias', e.target.value)} />
                                </div>
                                <div className="rp-field">
                                    <span className="rp-label">Ativo</span>
                                    <div className="md-ativo">
                                        <Switch checked={rascunho.ativo} onChange={v => mudar('ativo', v)} label="Modelo ativo" />
                                    </div>
                                </div>
                            </div>
                            {erro && <span className="rp-error">{erro}</span>}
                        </PanelSection>
                        <PanelSection title="Conteúdo">
                            <ModeloDocumentoEditor
                                key={`editar-${aberto.modeloDocumentoId}-${novoSeq}`}
                                conteudoInicial={rascunho.conteudo}
                                variaveis={variaveis}
                                onChange={json => setRascunho(r => ({ ...r, conteudo: json }))}
                                tipo={rascunho.tipo}
                                validadeDias={validadeDaFolha}
                                empresa={empresa}
                            />
                            <span className="md-hint">
                                Cabeçalho, título e rodapé saem sozinhos no PDF. Use "Inserir variável" para o que muda a cada
                                oportunidade e "Tabela de itens" para os produtos com o total.
                            </span>
                        </PanelSection>
                    </>
                )}

                {/* Histórico: seção no fim do modo ver (painel sem abas). */}
                {modo === 'ver' && aberto && (
                    <SecaoHistorico entidade="modelo_documento" id={aberto.modeloDocumentoId} />
                )}
            </RecordPanel>

            <NovoModeloModal
                aberta={galeriaAberta}
                existentes={modelos.map(m => m.nome)}
                variaveis={variaveis}
                onFechar={() => setGaleriaAberta(false)}
                onEscolher={aoEscolherNovo}
            />

            <Dialog
                open={confirmandoExclusao}
                onClose={() => setConfirmandoExclusao(false)}
                title={`Excluir o modelo "${aberto?.nome ?? ''}"?`}
                confirmLabel="Excluir"
                cancelLabel="Manter"
                danger
                busy={salvando}
                onConfirm={() => excluir()}
            >
                Os documentos já emitidos com ele não mudam: cada um guarda a própria cópia.
            </Dialog>
        </div>
    );
};

export default ModelosDocumentoPage;
